import React, { useState } from "react";
import { TrendingUp, TrendingDown, Minus, Info } from "lucide-react";
import { cn, formatPrice, formatCompactPrice } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useRestaurantCurrency } from "@/lib/hooks";

interface KPICardProps {
  title: string;
  value: string | number;
  previousValue?: string | number;
  format?: "number" | "currency" | "percentage";
  icon?: React.ReactNode;
  loading?: boolean;
  className?: string;
  description?: string;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  previousValue,
  format = "number",
  icon,
  loading = false,
  className,
  description,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const currency = useRestaurantCurrency();

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || "ontouchstart" in window);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const formatValue = (val: string | number): string => {
    if (typeof val === "string") return val;

    switch (format) {
      case "currency":
        return formatCompactPrice(val, currency);
      case "percentage":
        return `${val.toFixed(1)}%`;
      case "number":
      default:
        return new Intl.NumberFormat("fr-FR").format(val);
    }
  };

  const calculateGrowth = (): {
    percentage: number;
    trend: "up" | "down" | "neutral";
  } => {
    if (
      !previousValue ||
      typeof value !== "number" ||
      typeof previousValue !== "number"
    ) {
      return { percentage: 0, trend: "neutral" };
    }

    const growth = ((value - previousValue) / previousValue) * 100;

    if (growth > 0) return { percentage: growth, trend: "up" };
    if (growth < 0) return { percentage: Math.abs(growth), trend: "down" };
    return { percentage: 0, trend: "neutral" };
  };

  const { percentage, trend } = calculateGrowth();

  if (loading) {
    return (
      <div className={cn("bg-card rounded-lg border border-border p-6", className)}>
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "bg-card rounded-lg border border-border p-6 transition-shadow hover:shadow-md",
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-3 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-muted-foreground">
              {title}
            </p>
            {description &&
              (isMobile ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Info className="h-4 w-4 cursor-pointer text-muted-foreground" />
                  </PopoverTrigger>
                  <PopoverContent className="max-w-xs" side="bottom" align="start">
                    <p className="text-sm">{description}</p>
                  </PopoverContent>
                </Popover>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 cursor-help text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
          </div>

          {format === "currency" &&
          typeof value === "number" &&
          value >= 100000 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-2xl lg:text-3xl font-bold text-card-foreground leading-tight break-words cursor-help">
                    {formatValue(value)}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">
                    {formatPrice(value / 100, currency)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <p className="text-2xl lg:text-3xl font-bold text-card-foreground leading-tight break-words">
              {formatValue(value)}
            </p>
          )}

          {previousValue !== undefined && (
            <div className="flex items-center gap-2 flex-wrap">
              {trend === "up" && (
                <>
                  <TrendingUp className="h-4 w-4 text-success" />
                  <span className="text-sm text-success">
                    +{percentage.toFixed(1)}%
                  </span>
                </>
              )}
              {trend === "down" && (
                <>
                  <TrendingDown className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive">
                    -{percentage.toFixed(1)}%
                  </span>
                </>
              )}
              {trend === "neutral" && (
                <>
                  <Minus className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">0%</span>
                </>
              )}
              <span className="text-xs text-muted-foreground">
                vs période précédente
              </span>
            </div>
          )}
        </div>

        {icon && (
          <div className="ml-4">
            <div className="p-3 rounded-lg bg-primary-light">
              {React.cloneElement(icon as React.ReactElement, {
                className: "h-5 w-5 text-primary",
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
